import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useGlobalContext } from "../ContextApi/GlobalContext";

// Import your API function here
import { verifyCompanyEmail } from "../Backend/authFunction";

const slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.2 } },
};

const MailVerify = () => {
  const { dispatch } = useGlobalContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token"); // Get token from URL
  const [status, setStatus] = useState("verifying"); // 'verifying', 'success', 'error'
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus("error");
        return;
      }
      try {
        const response = await verifyCompanyEmail(token);
        if (response.success) {
          setStatus("success");
          dispatch({
            authToken: response?.data?.token,
            companyData: response?.data,
          });
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        console.error(error);
        setStatus("error");
      }
    };
    verifyToken();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB] font-sans p-6">
      <AnimatePresence mode="wait">
        {/* STATE 1: LOADING / VERIFYING */}
        {status === "verifying" && (
          <motion.div
            key="loading"
            variants={slideUp}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-center"
          >
            <div className="relative w-24 h-24 mx-auto mb-8">
              <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-[#E63946] rounded-full border-t-transparent animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="material-symbols-outlined text-[#0B1C2D] text-3xl">
                  verified_user
                </span>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-[#0B1C2D]">
              Verifying your email...
            </h2>
            <p className="text-gray-500 mt-2">
              Please wait while we secure your account.
            </p>
          </motion.div>
        )}

        {/* STATE 2: SUCCESS (Requested Button Included) */}
        {status === "success" && (
          <motion.div
            key="step5"
            variants={slideUp}
            initial="hidden"
            animate="visible"
            className="w-full max-w-md bg-white rounded-2xl shadow-xl p-10 text-center border border-gray-100"
          >
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 ring-8 ring-green-50">
              <span className="material-symbols-outlined text-green-600 text-4xl">
                check_circle
              </span>
            </div>

            <h2 className="text-3xl font-bold text-[#0B1C2D] mb-3">
              Account Created!
            </h2>

            <p className="text-gray-500 mb-8 leading-relaxed">
              Welcome to ContactHQ. You have been successfully registered and
              logged in.
            </p>

            <button
              onClick={() => navigate("/dashboard")}
              className="w-full bg-[#0B1C2D] hover:bg-[#152a40] text-white font-bold py-3.5 rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              Verify & Go to Dashboard
            </button>
          </motion.div>
        )}

        {/* STATE 3: ERROR (Invalid/Expired Link) */}
        {status === "error" && (
          <motion.div
            key="error"
            variants={slideUp}
            initial="hidden"
            animate="visible"
            className="w-full max-w-md bg-white rounded-2xl shadow-xl p-10 text-center border border-red-100"
          >
            <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6 ring-8 ring-red-50">
              <span className="material-symbols-outlined text-[#E63946] text-4xl">
                error
              </span>
            </div>

            <h2 className="text-2xl font-bold text-[#0B1C2D] mb-3">
              Verification Failed
            </h2>

            <p className="text-gray-500 mb-8 leading-relaxed">
              This link appears to be invalid or has expired. Please try logging
              in or requesting a new verification email.
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate("/register")}
                className="w-full bg-[#E63946] hover:bg-[#d12f3c] text-white font-bold py-3.5 rounded-lg shadow-lg hover:shadow-[#E63946]/20 transition-all"
              >
                Back to Register
              </button>
              <button
                onClick={() => navigate("/")} // Optional Home Link
                className="text-sm text-gray-400 hover:text-[#0B1C2D] font-medium"
              >
                Go to Homepage
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MailVerify;
